import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FilterControlsProps, FilterState } from './types';
import SortDropdown from './SortDropdown';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import styles from '@/styles/components/models-gallery/FilterControls.module.css';
import SearchInput from '@/components/ui/SearchInput';
import { tagsService, Tag } from '@/services/tagsService';

// Filter options
const TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'audio', label: 'Audio' },
  { value: 'multimodal', label: 'Multimodal' },
];

// TAG_OPTIONS will be loaded dynamically from the API

const PRICE_OPTIONS = [
  { value: 'free', label: 'Free', min: 0, max: 0 },
  { value: 'low', label: 'Low ($1-5)', min: 1, max: 5 },
  { value: 'medium', label: 'Medium ($6-15)', min: 6, max: 15 },
  { value: 'high', label: 'High ($16+)', min: 16, max: 1000 },
];

interface DropdownProps {
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  activeCount?: number;
  leftIcon?: React.ReactNode;
  contentClassName?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  label,
  isOpen,
  onToggle,
  children,
  activeCount,
  leftIcon,
  contentClassName,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isOpen) {
          onToggle();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    } else if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      onToggle();
    } else if (event.key === 'ArrowDown' && !isOpen) {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      <button
        type="button"
        className={`${styles.dropdownTrigger} ${isOpen ? styles.dropdownTriggerOpen : ''}`}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${label} filter`}
      >
        <span className={styles.dropdownLabel}>
          {leftIcon && (
            <span className={styles.dropdownLabelIcon} aria-hidden="true">
              {leftIcon}
            </span>
          )}
          {label}
          {typeof activeCount === 'number' && activeCount > 0 && (
            <Badge
              variant="primary"
              size="sm"
              className={styles.activeCountBadge}
            >
              {activeCount}
            </Badge>
          )}
          {typeof activeCount === 'number' && activeCount === 0 && (
            <span aria-hidden="true">&nbsp;</span>
          )}
        </span>
        <svg
          className={`${styles.dropdownIcon} ${isOpen ? styles.dropdownIconOpen : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`${styles.dropdownContent} ${contentClassName || ''}`}
          role="listbox"
        >
          {children}
        </div>
      )}
    </div>
  );
};

const FilterControls: React.FC<FilterControlsProps> = ({
  filters,
  onFiltersChange,
  sortOption,
  onSortChange,
}) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [tagQuery, setTagQuery] = useState<string>('');
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState<boolean>(true);

  // Fetch tags from API on component mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        setTagsLoading(true);
        const tags = await tagsService.getAvailableTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
        // Fallback tags will be used from the service
        setAvailableTags([]);
      } finally {
        setTagsLoading(false);
      }
    };

    fetchTags();
  }, []);

  const handleDropdownToggle = useCallback((dropdownName: string) => {
    setOpenDropdown(current =>
      current === dropdownName ? null : dropdownName
    );
  }, []);

  const handleTypeChange = useCallback(
    (type: string) => {
      const newTypes = filters.types.includes(type)
        ? filters.types.filter(t => t !== type)
        : [...filters.types, type];

      onFiltersChange({
        ...filters,
        types: newTypes,
      });
    },
    [filters, onFiltersChange]
  );

  const handleTagChange = useCallback(
    (tag: string) => {
      const newTags = filters.tags.includes(tag)
        ? filters.tags.filter(t => t !== tag)
        : [...filters.tags, tag];

      onFiltersChange({
        ...filters,
        tags: newTags,
      });
    },
    [filters, onFiltersChange]
  );

  const handlePriceChange = useCallback(
    (priceOption: (typeof PRICE_OPTIONS)[0]) => {
      onFiltersChange({
        ...filters,
        priceRange: {
          min: priceOption.min,
          max: priceOption.max,
        },
      });
      setOpenDropdown(null);
    },
    [filters, onFiltersChange]
  );

  const clearAllFilters = useCallback(() => {
    onFiltersChange({
      types: [],
      tags: [],
      priceRange: { min: 0, max: 1000 },
    });
    setOpenDropdown(null);
    setTagQuery('');
  }, [onFiltersChange]);

  const hasActiveFilters =
    filters.types.length > 0 ||
    filters.tags.length > 0 ||
    filters.priceRange.min > 0 ||
    filters.priceRange.max < 1000;

  const getActivePriceOption = () => {
    return PRICE_OPTIONS.find(
      option =>
        option.min === filters.priceRange.min &&
        option.max === filters.priceRange.max
    );
  };

  return (
    <div
      className={styles.filterContainer}
      role="group"
      aria-labelledby="filter-heading"
      aria-describedby="filter-instructions"
    >
      <h3 id="filter-heading" className="sr-only">
        Filter Options
      </h3>
      <div id="filter-instructions" className="sr-only">
        Use the dropdown menus to filter models by type, tags, and price.
        Multiple filters can be applied simultaneously.
      </div>

      {/* Type Filter */}
      <Dropdown
        label="Type"
        isOpen={openDropdown === 'type'}
        onToggle={() => handleDropdownToggle('type')}
        activeCount={filters.types.length}
      >
        {TYPE_OPTIONS.map(option => (
          <label
            key={option.value}
            className={styles.dropdownOption}
            role="option"
            aria-selected={filters.types.includes(option.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTypeChange(option.value);
              }
            }}
            tabIndex={0}
          >
            <input
              type="checkbox"
              checked={filters.types.includes(option.value)}
              onChange={() => handleTypeChange(option.value)}
              className={styles.checkbox}
              tabIndex={-1}
            />
            <span className={styles.checkboxLabel}>{option.label}</span>
          </label>
        ))}
      </Dropdown>

      {/* Tags Filter */}
      <Dropdown
        label="Tags"
        isOpen={openDropdown === 'tags'}
        onToggle={() => handleDropdownToggle('tags')}
        activeCount={filters.tags.length}
        leftIcon={
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.59 13.41L12 4.83C11.63 4.46 11.14 4.26 10.63 4.26H5C3.9 4.26 3 5.16 3 6.26V11.89C3 12.4 3.2 12.89 3.59 13.26L12.17 21.84C12.95 22.62 14.21 22.62 14.99 21.84L20.58 16.25C21.36 15.47 21.36 14.21 20.59 13.41ZM6.5 9.26C5.67 9.26 5 8.59 5 7.76C5 6.93 5.67 6.26 6.5 6.26C7.33 6.26 8 6.93 8 7.76C8 8.59 7.33 9.26 6.5 9.26Z"
              fill="currentColor"
            />
          </svg>
        }
        contentClassName={styles.tagsDropdownContent}
      >
        {/* Search within tags */}
        <div className={styles.dropdownSearchWrapper}>
          <SearchInput
            value={tagQuery}
            onChange={setTagQuery}
            placeholder="Search item"
            ariaLabel="Search tag"
            inputSize="sm"
          />
        </div>

        {filters.tags.length > 0 && (
          <div className={styles.selectedChipsRow}>
            {filters.tags.map(tag => {
              const tagInfo = availableTags.find(t => t.id === tag);
              return (
                <button
                  key={`chip-${tag}`}
                  type="button"
                  className={styles.tagChip}
                  onClick={() => handleTagChange(tag)}
                  aria-label={`Remove ${tagInfo?.name || tag}`}
                >
                  <span className={styles.tagChipLabel}>
                    {tagInfo?.name || tag}
                  </span>
                  <span className={styles.tagChipIcon} aria-hidden="true">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <path
                        d="M6 6L18 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div className={styles.dropdownDivider} />
        <div className={styles.optionsList}>
          {tagsLoading ? (
            <div className={styles.loadingMessage}>Loading tags...</div>
          ) : (
            availableTags
              .filter(tag =>
                tagQuery.trim().length === 0
                  ? true
                  : tag.name.toLowerCase().includes(tagQuery.toLowerCase()) ||
                    tag.id.toLowerCase().includes(tagQuery.toLowerCase())
              )
              .map(tag => (
                <button
                  key={tag.id}
                  type="button"
                  className={styles.listItem}
                  onClick={() => handleTagChange(tag.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleTagChange(tag.id);
                    }
                  }}
                  role="option"
                  aria-selected={filters.tags.includes(tag.id)}
                  tabIndex={0}
                >
                  <span className={styles.listItemLabel}>{tag.name}</span>
                </button>
              ))
          )}
        </div>
      </Dropdown>

      {/* Price Filter */}
      <Dropdown
        label="Price"
        isOpen={openDropdown === 'price'}
        onToggle={() => handleDropdownToggle('price')}
        activeCount={
          getActivePriceOption() && getActivePriceOption()?.value !== 'free'
            ? 1
            : 0
        }
      >
        {PRICE_OPTIONS.map(option => {
          const isSelected =
            option.min === filters.priceRange.min &&
            option.max === filters.priceRange.max;
          return (
            <button
              key={option.value}
              type="button"
              className={`${styles.dropdownOption} ${isSelected ? styles.dropdownOptionSelected : ''}`}
              onClick={() => handlePriceChange(option)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePriceChange(option);
                }
              }}
              role="option"
              aria-selected={isSelected}
              tabIndex={0}
            >
              {option.label}
            </button>
          );
        })}
      </Dropdown>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAllFilters}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              clearAllFilters();
            }
          }}
          className={styles.clearButton}
          aria-label="Clear all filters"
        >
          Clear
        </Button>
      )}

      {/* Sort Dropdown */}
      <SortDropdown
        value={sortOption}
        onChange={onSortChange}
        className={styles.sortDropdown}
      />

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div
          className={styles.activeFilters}
          aria-live="polite"
          aria-atomic="true"
          aria-labelledby="active-filters-heading"
        >
          <h4 id="active-filters-heading" className="sr-only">
            Active Filters
          </h4>
          {filters.types.map(type => (
            <Badge
              key={`type-${type}`}
              variant="secondary"
              size="sm"
              className={styles.activeFilterBadge}
              aria-label={`Type filter: ${TYPE_OPTIONS.find(opt => opt.value === type)?.label}`}
            >
              {TYPE_OPTIONS.find(opt => opt.value === type)?.label}
            </Badge>
          ))}
          {filters.tags.map(tag => {
            const tagInfo = availableTags.find(t => t.id === tag);
            return (
              <Badge
                key={`tag-${tag}`}
                variant="secondary"
                size="sm"
                className={styles.activeFilterBadge}
                aria-label={`Tag filter: ${tagInfo?.name || tag}`}
              >
                {tagInfo?.name || tag}
              </Badge>
            );
          })}
          {getActivePriceOption() &&
            getActivePriceOption()?.value !== 'free' && (
              <Badge
                variant="secondary"
                size="sm"
                className={styles.activeFilterBadge}
                aria-label={`Price filter: ${getActivePriceOption()?.label}`}
              >
                {getActivePriceOption()?.label}
              </Badge>
            )}
        </div>
      )}
    </div>
  );
};

export default FilterControls;
